import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PersonName, usePersonLabel } from '../../ui/PersonName';
import { shortAddress } from '../../ui/people';
import { dmNameFor } from '../analytics/conductorName';
import { AttachmentChip } from '../../ui/AttachmentChip';
import { attachmentLabels } from '../../ui/attachmentLabel';
import { Link, useParams } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { canSeeStore } from '../../data/scope';
import { getAllStores } from '../../data/stores';
import {
  buildAnswersFromRows,
  downloadAuditAttachment,
  downloadAuditPdf,
  getAttachmentsForAudit,
  getAuditRecord,
  getNotesForAudit,
  getResponsesForAudit,
  type AuditAttachmentRow,
  type AuditNoteRow,
  type AuditRecord,
} from '../../data/audits';
import { getTasksForAudit, type ServerTask } from '../../data/tasks';
import { getTemplateById } from '../../data/templates';
import {
  definitionHasScore,
  isQuestionVisible,
  isSectionVisible,
  subsectionTitlesFor,
  type AnswerMap,
  type Question,
  type SampleEntryValue,
  type TemplateDefinition,
} from '../../audit/template';
import {
  entryHasContent,
  failedFields,
  formatAnswerValue,
  formatFieldValue,
  isFailedAnswer,
} from '../../audit/findings';
import type { Store } from '../../data/types';
import { formatDateTime, formatDay } from '../../ui/dates';
import { ActionButton } from '../../ui/ActionButton';
import { exportFileName, saveBlob } from '../../ui/download';
import { dataViaApi } from '../../api/mode';
import { easternIsoDay } from '../../data/taskRules';
import { taskStatusLabel } from '../../data/taskRules';
import { scorePct } from '../../ui/scoreText';

type ViewMode = 'findings' | 'full';

interface LoadedRecord {
  record: AuditRecord;
  store: Store | null;
  definition: TemplateDefinition | null;
  /** The pinned version's scoring mode ('none' = a no-score form). */
  scoringMode: string;
  answers: AnswerMap;
  notes: AuditNoteRow[];
  tasks: ServerTask[];
  attachments: AuditAttachmentRow[];
}

/**
 * Read-only record of a submitted audit, rebuilt from SharePoint: answers,
 * score, and the notes/tasks attached to each question. This is the view
 * store teams work findings from and where DMs see their score (post-submit
 * everyone sees it — his 2026-08-10 ruling).
 */
export function AuditRecordPage() {
  const { auditId } = useParams<{ auditId: string }>();
  const { user } = useAppUser();
  const [data, setData] = useState<LoadedRecord | 'missing' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ViewMode>('findings');
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    const id = Number(auditId);
    if (!auditId || !Number.isInteger(id) || id <= 0) {
      setData('missing');
      return;
    }
    Promise.all([
      getAuditRecord(id),
      // includeInactive: a divested store's past audits must still resolve.
      getAllStores({ includeInactive: true }),
      getResponsesForAudit(id),
      getNotesForAudit(id),
      getTasksForAudit(id),
      // Attachment listing is an enhancement; a failure shouldn't kill the page.
      getAttachmentsForAudit(id).catch(() => [] as AuditAttachmentRow[]),
    ])
      .then(async ([record, stores, responses, notes, tasks, attachments]) => {
        if (!record) {
          setData('missing');
          return;
        }
        const template = await getTemplateById(record.templateId);
        setData({
          record,
          store: stores.find((s) => Number(s.id) === record.storeId) ?? null,
          definition: template?.definition ?? null,
          scoringMode: template?.settings.scoringMode ?? '',
          answers: buildAnswersFromRows(responses).answers,
          notes,
          tasks,
          attachments,
        });
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [auditId]);

  const findingCount = useMemo(() => {
    if (!data || data === 'missing' || !data.definition) return 0;
    let count = 0;
    for (const section of data.definition.sections) {
      for (const q of section.questions) {
        if (
          isQuestionVisible(data.definition, section, q, data.answers) &&
          hasFinding(q, data)
        ) {
          count++;
        }
      }
    }
    return count;
  }, [data]);
  // Nothing to show under Findings (a score-less visit, a clean audit): open on the
  // full record instead of an empty tab (UI review 3, S2 / U44). Once — a tap wins after.
  // Per audit (Back / Forward to another record reuses this page — QA), before paint.
  const openedOn = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!data || data === 'missing' || !data.definition || openedOn.current === data.record.id) return;
    openedOn.current = data.record.id;
    setMode(findingCount === 0 ? 'full' : 'findings');
  }, [data, findingCount]);

  if (error) {
    return (
      <div className="page-message error">
        <p>Could not load this audit: {error}</p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }
  if (data === 'missing') {
    return (
      <div className="page-message">
        <p>
          This audit isn’t available — it may still be in progress (stores see
          an audit once it’s submitted), outside your access, or removed.
        </p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }
  if (data === null || !user) {
    return <div className="page-message">Loading audit record…</div>;
  }

  const { record, store, definition, scoringMode, notes, tasks } = data;
  // A no-score form (e.g. the DM Visit report) shows no Score block at all.
  const hasScore = definition === null || definitionHasScore(definition, scoringMode);

  if (store && !canSeeStore(user, store)) {
    return (
      <div className="page-message">
        <p>This audit belongs to a store outside your access.</p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }
  if (record.status !== 'submitted') {
    return (
      <div className="page-message">
        <p>
          This audit hasn’t been submitted yet — results appear here once the
          auditor submits it.
        </p>
        <Link to="/audits">Back to audits</Link>
      </div>
    );
  }

  const generalNotes = notes.filter((n) => n.questionId === null);
  const generalTasks = tasks.filter((t) => t.questionId === '');

  return (
    <div className="audit-page">
      <div className="page-head">
        <div>
          <h1>
            {store
              ? `${store.storeNumber} — ${store.name}`
              : 'Unknown store'}
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            {definition?.name ?? 'Unknown form'} · conducted
            by <ConductorName upn={record.auditorUpn} store={store} />
            {record.startedAt && (
              <>
                {' · '}
                <span className="nowrap">started {formatDateTime(record.startedAt)}</span>
              </>
            )}{' '}
            · <span className="nowrap">submitted {formatDateTime(record.submittedAt)}</span>
          </p>
        </div>
        <div className="page-head-tools">
          {dataViaApi() && (
            <ActionButton
              className="btn"
              busyLabel="Making PDF…"
              title="The whole audit — every answer, note, task and photo — as a PDF"
              onAction={async () => {
                setPdfError(null);
                try {
                  const name = [store?.storeNumber ?? `store-${record.storeId}`, definition?.name ?? 'audit', submittedDay(record.submittedAt)].join(' ');
                  saveBlob(exportFileName(name, 'pdf'), await downloadAuditPdf(record.id));
                } catch (err) {
                  setPdfError(err instanceof Error ? err.message : String(err));
                }
              }}
            >
              ⬇ Download PDF
            </ActionButton>
          )}
          <Link className="btn" to="/audits">
            All audits
          </Link>
        </div>
      </div>
      {pdfError && <p className="error" role="alert">Couldn’t make the PDF: {pdfError}</p>}

      {hasScore && (
      <div className="audit-section">
        <div className="audit-section-body record-summary">
          <div className="record-score">
            <span className="muted">Score</span>
            <strong>
              {record.score === null ? 'n/a' : scorePct(record.score)}
            </strong>
            {record.criticalFailed && (
              <span className="error">critical failure</span>
            )}
          </div>
          {/* Full record lists each section's score in its section list instead (UI-6 review). */}
          {record.sectionScores && definition && mode !== 'full' && (
            <details className="record-sections">
              <summary>Section scores</summary>
              <ul>
                {Object.entries(record.sectionScores).map(([id, s]) => (
                  <li key={id}>
                    {definition.sections.find((x) => x.id === id)?.title ?? id}:{' '}
                    {s === null ? 'not scored' : scorePct(s)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
      )}

      {(generalNotes.length > 0 || generalTasks.length > 0) && (
        <div className="audit-section">
          <div className="audit-section-body">
            <h3 style={{ marginTop: 0 }}>General notes &amp; tasks</h3>
            {generalNotes.map((n) => (
              <NoteLine note={n} key={n.id} />
            ))}
            {generalTasks.map((t) => (
              <TaskLine task={t} key={t.id} />
            ))}
          </div>
        </div>
      )}

      {definition === null ? (
        <p className="error">
          The template this audit was conducted against could not be loaded,
          so per-question detail is unavailable.
        </p>
      ) : (
        <>
          <div className="record-toolbar">
            <div className="seg-group" role="group" aria-label="Detail level">
              <button
                className={mode === 'findings' ? 'seg seg-on' : 'seg'}
                onClick={() => setMode('findings')}
              >
                Findings ({findingCount})
              </button>
              <button
                className={mode === 'full' ? 'seg seg-on' : 'seg'}
                onClick={() => setMode('full')}
              >
                Full record
              </button>
            </div>
            {mode === 'findings' && (
              <span className="muted">
                Failed items plus anything with a note or task.
              </span>
            )}
          </div>

          {mode === 'findings' && findingCount === 0 && (
            <p className="muted">
              No findings — nothing failed and no notes or tasks were
              recorded. 🎉
            </p>
          )}

          <RecordSections key={mode} definition={definition} data={data} mode={mode} hasScore={hasScore} />
        </>
      )}
    </div>
  );
}

/** Nothing recorded for a question: no answer, no note, task or file (UI review U46). */
function isUnanswered(q: Question, data: LoadedRecord): boolean {
  const v = data.answers[q.id];
  // A sample group of empty entries ("+ Add entry" never filled) is blank too — the record says "No entries recorded".
  const blank =
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && !(v as unknown[]).some((e) => (typeof e === 'string' ? e.trim() !== '' : !!e && typeof e === 'object' && entryHasContent(e as SampleEntryValue))));
  return (
    blank &&
    !data.notes.some((n) => n.questionId === q.id) &&
    !data.tasks.some((t) => t.questionId === q.id) &&
    !data.attachments.some((a) => a.questionId === q.id)
  );
}

/**
 * The record's sections. Findings: every section with a finding, open.
 * Full record (owner ruling 2026-10-29, UI review V45 / U40): a section list
 * at the top (each with its score) that opens and jumps to a section, the
 * first section open and the rest collapsed, "Expand all"; unanswered
 * questions fold into "Show N not answered" at the end of their section (U46).
 */
export function RecordSections({ definition, data, mode, hasScore = true }: { definition: TemplateDefinition; data: LoadedRecord; mode: ViewMode; hasScore?: boolean }) {
  const { answers, notes, tasks, attachments, record } = data;
  const full = mode === 'full';
  const sections = definition.sections.flatMap((section) => {
    if (!isSectionVisible(definition, section, answers)) return [];
    const questions = section.questions.filter(
      (q) => isQuestionVisible(definition, section, q, answers) && (full || hasFinding(q, data)),
    );
    return questions.length ? [{ section, questions }] : [];
  });
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(sections[0] ? [sections[0].section.id] : []));
  const [showBlank, setShowBlank] = useState<ReadonlySet<string>>(new Set());
  const isOpen = (id: string) => !full || open.has(id);
  const allOpen = sections.every((x) => open.has(x.section.id));
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const jumpTo = (id: string) => {
    setOpen((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => {
      const el = document.getElementById(`rec-sec-${id}`);
      el?.scrollIntoView({ block: 'start' });
      el?.focus({ preventScroll: true });
    });
  };
  const scoreOf = (id: string) => {
    // A no-score form (the DM Visit) shows no section scores, like its Score block.
    if (!hasScore) return null;
    const sc = record.sectionScores?.[id];
    // No scored question in it: say so, like the PDF's "n/a" (UI review 3, D7) — not a blank.
    if (sc === null) return 'not scored';
    return sc === undefined ? null : scorePct(sc);
  };

  return (
    <>
      {full && sections.length > 1 && (
        <nav className="record-jump" aria-label="Sections">
          <div className="record-jump-head">
            <strong className="record-jump-label">Sections</strong>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => setOpen(allOpen ? new Set() : new Set(sections.map((x) => x.section.id)))}
            >
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
          <ol>
            {sections.map(({ section }) => (
              <li key={section.id}>
                <button type="button" className="link-button" onClick={() => jumpTo(section.id)}>
                  {section.title}
                </button>
                {scoreOf(section.id) && <span className="muted"> · {scoreOf(section.id)}</span>}
              </li>
            ))}
          </ol>
        </nav>
      )}
      {sections.map(({ section, questions }) => {
        const blank = full ? questions.filter((q) => isUnanswered(q, data)) : [];
        const showingBlank = showBlank.has(section.id);
        const shown = showingBlank || blank.length < 2 ? questions : questions.filter((q) => !blank.includes(q));
        const titles = subsectionTitlesFor(section, shown);
        const opened = isOpen(section.id);
        return (
          <div className={`audit-section${full && !opened ? ' audit-section-collapsed' : ''}`} key={section.id}>
            <div className="audit-section-body">
              <h2 className="record-section-title" id={`rec-sec-${section.id}`} tabIndex={-1}>
                {full ? (
                  <button type="button" className="record-section-toggle" aria-expanded={opened} onClick={() => toggle(section.id)}>
                    <span aria-hidden="true">{opened ? '▾' : '▸'}</span> {section.title}
                    {scoreOf(section.id) && <span className="muted record-section-score">{scoreOf(section.id)}</span>}
                  </button>
                ) : (
                  section.title
                )}
              </h2>
              {opened && (
                <>
                  {shown.map((q) => (
                    <Fragment key={q.id}>
                      {/* Subsection titles show on the record too (owner ruling 2026-11-02). */}
                      {titles.get(q.id) && <h3 className="record-subsection">{titles.get(q.id)}</h3>}
                      <RecordQuestion
                        question={q}
                        answers={answers}
                        notes={notes.filter((n) => n.questionId === q.id)}
                        tasks={tasks.filter((t) => t.questionId === q.id)}
                        attachments={attachments.filter((a) => a.questionId === q.id)}
                        failedOnly={!full}
                      />
                    </Fragment>
                  ))}
                  {blank.length >= 2 && (
                    <button
                      type="button"
                      className="btn btn-small record-blank-toggle"
                      onClick={() =>
                        setShowBlank((prev) => {
                          const next = new Set(prev);
                          if (next.has(section.id)) next.delete(section.id);
                          else next.add(section.id);
                          return next;
                        })
                      }
                    >
                      {showingBlank ? `Hide the ${blank.length} not answered` : `Show ${blank.length} not answered`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

/** The Eastern day an audit was submitted ("2026-10-27"), for the PDF's file name. */
function submittedDay(iso: string): string {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? '' : easternIsoDay(new Date(t));
}

/** A question counts as a finding if it failed or carries a note/task. */
function hasFinding(q: Question, data: LoadedRecord): boolean {
  return (
    isFailedAnswer(q, data.answers[q.id]) ||
    data.notes.some((n) => n.questionId === q.id) ||
    data.tasks.some((t) => t.questionId === q.id)
  );
}

function RecordQuestion({
  question,
  answers,
  notes,
  tasks,
  attachments,
  failedOnly,
}: {
  question: Question;
  answers: AnswerMap;
  notes: AuditNoteRow[];
  tasks: ServerTask[];
  attachments: AuditAttachmentRow[];
  failedOnly: boolean;
}) {
  const value = answers[question.id];
  const failed = isFailedAnswer(question, value);
  // Sample-tagged items render inside their entry; the rest sit here.
  const qNotes = notes.filter((n) => n.sampleIndex === null);
  const qTasks = tasks.filter((t) => t.sampleIndex === null);
  const qAttachments = attachments.filter((a) => a.sampleIndex === null);

  return (
    <div className={failed ? 'record-q record-q-failed' : 'record-q'}>
      <div className="record-q-head">
        <span className="record-q-text">{question.text}</span>
        {failed && <span className="fail-badge">Fail</span>}
      </div>
      {question.type === 'sample_group' ? (
        <SampleReadback
          question={question}
          value={Array.isArray(value) ? (value as never[]) : []}
          failedOnly={failedOnly}
          notes={notes}
          tasks={tasks}
          attachments={attachments}
        />
      ) : (
        <div className="record-answer">{formatAnswerValue(question, value)}</div>
      )}
      <AttachmentLinks attachments={qAttachments} label={question.text} />
      {qNotes.map((n) => (
        <NoteLine note={n} key={n.id} />
      ))}
      {qTasks.map((t) => (
        <TaskLine task={t} key={t.id} />
      ))}
    </div>
  );
}

function AttachmentLinks({ attachments, label }: { attachments: AuditAttachmentRow[]; label: string }) {
  if (attachments.length === 0) return null;
  const names = attachmentLabels(attachments, label);
  return (
    <div className="record-attachments">
      {attachments.map((a) => (
        <RecordAttachment key={a.id} attachment={a} name={names.get(a.id) ?? a.filename} />
      ))}
    </div>
  );
}

/** One uploaded file on the record (shared chip: thumbnail/lightbox/PDF tab). */
function RecordAttachment({ attachment, name }: { attachment: AuditAttachmentRow; name: string }) {
  return (
    <AttachmentChip
      kind={attachment.kind}
      filename={name}
      url={attachment.url}
      download={() => downloadAuditAttachment(attachment.id)}
    />
  );
}

function SampleReadback({
  question,
  value,
  failedOnly,
  notes,
  tasks,
  attachments,
}: {
  question: Question;
  value: Array<Record<string, string | undefined>>;
  failedOnly: boolean;
  notes: AuditNoteRow[];
  tasks: ServerTask[];
  attachments: AuditAttachmentRow[];
}) {
  const entryHasItems = (index: number): boolean =>
    notes.some((n) => n.sampleIndex === index) ||
    tasks.some((t) => t.sampleIndex === index) ||
    attachments.some((a) => a.sampleIndex === index);
  const entries = value
    .map((entry, index) => ({ entry: entry ?? {}, index }))
    .filter(({ entry }) => entryHasContent(entry))
    // In findings mode, keep samples that failed OR carry a note/task/file.
    .filter(
      ({ entry, index }) =>
        !failedOnly ||
        failedFields(question, entry).length > 0 ||
        entryHasItems(index),
    );
  if (entries.length === 0) {
    return <div className="record-answer muted">No entries recorded.</div>;
  }
  return (
    <div className="record-samples">
      {entries.map(({ entry, index }) => {
        const failedIds = new Set(failedFields(question, entry).map((f) => f.id));
        return (
          <div className="record-sample" key={index}>
            <div className="record-sample-title">
              Entry {index + 1}
              {failedIds.size > 0 && <span className="fail-badge">Fail</span>}
            </div>
            <dl>
              {(question.fields ?? [])
                .filter((f) => entry[f.id] !== undefined && entry[f.id] !== '')
                .map((f) => (
                  <div className="record-field" key={f.id}>
                    <dt>{f.label}</dt>
                    <dd className={failedIds.has(f.id) ? 'error' : ''}>
                      {formatFieldValue(f, entry[f.id])}
                    </dd>
                  </div>
                ))}
            </dl>
            <AttachmentLinks
              attachments={attachments.filter((a) => a.sampleIndex === index)}
              label={`Entry ${index + 1}`}
            />
            {notes
              .filter((n) => n.sampleIndex === index)
              .map((n) => (
                <NoteLine note={n} key={n.id} />
              ))}
            {tasks
              .filter((t) => t.sampleIndex === index)
              .map((t) => (
                <TaskLine task={t} key={t.id} />
              ))}
          </div>
        );
      })}
    </div>
  );
}

function NoteLine({ note }: { note: AuditNoteRow }) {
  return (
    <div className="q-note">
      🗒 {note.text}
      <span className="muted"> — <PersonName upn={note.author} /></span>
    </div>
  );
}

function TaskLine({ task }: { task: ServerTask }) {
  return (
    <div className="q-note q-note-task">
      {/* The ticket: how it was resolved — proof, comments (owner ask 2026-10-21: Executives review these too). */}
      ☑ Task: <Link to={`/tasks/${task.id}`}>{task.title}</Link>
      <span className="muted">
        {' '}
        → <PersonName upn={task.assignedTo} />, due {formatDay(task.dueDate) || '—'}
      </span>
      <span className={`task-badge task-status-${task.status}`}>
        {taskStatusLabel(task.status)}
      </span>
      {task.status === 'complete' && task.completedByName && (
        <span className="muted"> by {task.completedByName}</span>
      )}
    </div>
  );
}

/**
 * Who conducted it: the usual person label (store account → store, else
 * the directory name); while that is still only the short address, the DM
 * name on the audited store's row when the conductor is that store's DM
 * (exec run E3 — [SIM] DMs have no directory name).
 */
function ConductorName({ upn, store }: { upn: string; store: Store | null }) {
  const label = usePersonLabel(upn);
  const dm = store && label === shortAddress(upn) ? dmNameFor([store], upn) : null;
  return <span title={upn.trim() || undefined}>{dm ?? label}</span>;
}
